import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Switch, Table, message } from 'antd';
import { FileOutlined, FolderOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { MenuFormModal } from './MenuFormModal';
import {
  MENU_TYPE_LABEL,
  buildMenuTree,
  createMenu,
  deleteMenu,
  getMenuDetail,
  getMenus,
  updateMenu,
  updateMenuStatus,
  type CreateMenuBody,
  type MenuItem
} from './menuManageData';

function rowKey(row: MenuItem) {
  return row.menuNo?.trim() || row.routePath?.trim() || row.displayName || '';
}

function dash(value?: string | number | null) {
  if (value == null) return '—';
  const text = String(value).trim();
  return text ? text : '—';
}

function labelOf(map: Record<string, string>, value?: string) {
  if (!value) return '—';
  return map[value] || value;
}

function getLevel(record: MenuItem, tree: MenuItem[]): number {
  const key = rowKey(record);
  const find = (nodes: MenuItem[], depth: number): number | null => {
    for (const node of nodes) {
      if (rowKey(node) === key) return depth;
      if (node.children?.length) {
        const hit = find(node.children, depth + 1);
        if (hit != null) return hit;
      }
    }
    return null;
  };
  return find(tree, 0) ?? 0;
}

const MENU_NO_PATTERN = /^[0-9]{21}$/;

function replaceMenuStatus(nodes: MenuItem[], menuNo: string, status: string): MenuItem[] {
  return nodes.map((node) => {
    const current = node.menuNo?.trim() === menuNo ? { ...node, status } : node;
    if (!current.children?.length) return current;
    return { ...current, children: replaceMenuStatus(current.children, menuNo, status) };
  });
}

function collectExpandable(nodes: MenuItem[], keys: string[]) {
  nodes.forEach((node) => {
    if (!node.children?.length) return;
    const key = rowKey(node);
    if (key) keys.push(key);
    collectExpandable(node.children, keys);
  });
}

export function MenuManage() {
  const [tree, setTree] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingMenuNo, setEditingMenuNo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState<string[]>([]);
  const statusUpdatingRef = useRef(new Set<string>());

  const loadMenus = () => {
    setLoading(true);
    getMenus()
      .then((res) => {
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || '菜单列表加载失败');
          setTree([]);
          setExpandedRowKeys([]);
          return;
        }
        const next = buildMenuTree(Array.isArray(res.content) ? res.content : []);
        const keys: string[] = [];
        collectExpandable(next, keys);
        setTree(next);
        setExpandedRowKeys(keys);
      })
      .catch(() => {
        message.error('菜单列表加载失败');
        setTree([]);
        setExpandedRowKeys([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadMenus();
  }, []);

  const changeStatus = async (row: MenuItem, checked: boolean) => {
    const menuNo = row.menuNo?.trim() ?? '';
    const nextStatus = checked ? 'ACTIVE' : 'DISABLED';
    if (!MENU_NO_PATTERN.test(menuNo)) {
      message.warning('菜单编码须为 21 位数字');
      return;
    }
    if (row.status === nextStatus || statusUpdatingRef.current.has(menuNo)) return;
    statusUpdatingRef.current.add(menuNo);
    setStatusUpdating((prev) => [...prev, menuNo]);
    try {
      const detailRes = await getMenuDetail(menuNo);
      if (detailRes.code !== 200 || !detailRes.content?.data) {
        if (detailRes.code !== -1) message.error(detailRes.message || '菜单详情加载失败');
        return;
      }
      const hash = detailRes.content.configHash;
      if (typeof hash !== 'string' || !hash) {
        message.warning('缺少配置校验值，请稍后重试');
        return;
      }
      const res = await updateMenuStatus(menuNo, nextStatus, hash);
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || '状态修改失败');
        return;
      }
      const returned = res.content?.data?.status;
      const status = returned === 'ACTIVE' || returned === 'DISABLED' ? returned : nextStatus;
      setTree((prev) => replaceMenuStatus(prev, menuNo, status));
      message.success(res.message || (status === 'ACTIVE' ? '已启用' : '已停用'));
    } catch {
      message.error('状态修改失败');
    } finally {
      statusUpdatingRef.current.delete(menuNo);
      setStatusUpdating((prev) => prev.filter((code) => code !== menuNo));
    }
  };

  const confirmDelete = (row: MenuItem) => {
    const menuNo = row.menuNo?.trim() ?? '';
    if (!MENU_NO_PATTERN.test(menuNo)) {
      message.warning('菜单编码须为 21 位数字');
      return;
    }
    const name = row.displayName?.trim() || menuNo;
    Modal.confirm({
      title: '确认删除',
      content: `确定删除菜单「${name}」吗？`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      centered: true,
      onOk: async () => {
        const detailRes = await getMenuDetail(menuNo);
        if (detailRes.code !== 200) {
          if (detailRes.code !== -1) message.error(detailRes.message || '菜单详情加载失败');
          return;
        }
        const hash = detailRes.content?.configHash;
        if (typeof hash !== 'string' || !hash) {
          message.warning('缺少配置校验值，请稍后重试');
          return;
        }
        const res = await deleteMenu(menuNo, hash);
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || (res.code === 409 ? '配置已变化，请重新确认删除' : '菜单删除失败'));
          return;
        }
        message.success(res.message || '菜单已删除');
        loadMenus();
      }
    });
  };

  const closeForm = () => {
    if (submitting) return;
    setFormOpen(false);
    setEditingMenuNo(null);
  };

  const handleSubmit = (body: CreateMenuBody, edit?: { menuNo: string; expectedConfigHash: string }) => {
    setSubmitting(true);
    const request = edit
      ? updateMenu(edit.menuNo, { ...body, expectedConfigHash: edit.expectedConfigHash })
      : createMenu(body);
    request
      .then((res) => {
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || (edit ? '菜单修改失败' : '菜单创建失败'));
          return;
        }
        message.success(res.message || (edit ? '菜单已更新' : '菜单已创建'));
        setFormOpen(false);
        setEditingMenuNo(null);
        loadMenus();
      })
      .catch(() => message.error(edit ? '菜单修改失败' : '菜单创建失败'))
      .finally(() => setSubmitting(false));
  };

  const rootIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    tree.forEach((node, index) => {
      const key = rowKey(node);
      if (key) map.set(key, index + 1);
    });
    return map;
  }, [tree]);

  const toggleExpand = (row: MenuItem) => {
    const key = rowKey(row);
    setExpandedRowKeys((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]));
  };

  const columns: ColumnsType<MenuItem> = [
    {
      title: '序号',
      width: 64,
      render: (_, row) => (getLevel(row, tree) === 0 ? rootIndexMap.get(rowKey(row)) ?? '—' : '—')
    },
    {
      title: '菜单编码',
      dataIndex: 'menuNo',
      width: 210,
      render: (value: string) => <code className='admin-dict-code'>{dash(value)}</code>
    },
    {
      title: '菜单名称',
      dataIndex: 'displayName',
      width: 220,
      render: (value: string, row) => {
        const level = getLevel(row, tree);
        const isDirectory = row.menuType === 'DIRECTORY';
        return (
          <div
            className={`admin-menu-name-cell${level > 0 ? ' is-child' : ''}${isDirectory ? ' is-directory' : ''}`}
            style={{ paddingLeft: level * 36 }}
          >
            {level > 0 ? <span className='admin-menu-indent-rail' aria-hidden /> : null}
            {isDirectory ? (
              <FolderOutlined className='admin-menu-kind-icon is-directory' />
            ) : (
              <FileOutlined className='admin-menu-kind-icon' />
            )}
            <span className='admin-cell-primary'>{dash(value)}</span>
          </div>
        );
      }
    },
    {
      title: '类型',
      dataIndex: 'menuType',
      width: 110,
      render: (value: string) => (
        <span className={`admin-menu-type admin-menu-type--${value === 'DIRECTORY' ? 'directory' : 'menu'}`}>
          {labelOf(MENU_TYPE_LABEL, value)}
        </span>
      )
    },
    {
      title: '路由路径',
      dataIndex: 'routePath',
      ellipsis: true,
      render: (value: string) => <code className='admin-dict-code'>{dash(value)}</code>
    },
    {
      title: '组件键',
      dataIndex: 'componentKey',
      width: 160,
      ellipsis: true,
      render: (value: string) => dash(value)
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      width: 80,
      align: 'center',
      render: (value: number) => (value == null ? '—' : value)
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      align: 'center',
      render: (status: string | undefined, row) => {
        const menuNo = row.menuNo?.trim() ?? '';
        const updating = statusUpdating.includes(menuNo);
        return (
          <Switch
            size='small'
            checked={status === 'ACTIVE'}
            loading={updating}
            disabled={(status !== 'ACTIVE' && status !== 'DISABLED') || updating}
            onChange={(checked) => changeStatus(row, checked)}
          />
        );
      }
    },
    {
      title: '操作',
      key: 'actions',
      width: 170,
      render: (_, row) => {
        const hasChildren = !!row.children?.length;
        const expanded = expandedRowKeys.includes(rowKey(row));
        return (
          <div className='admin-row-actions'>
            <button
              type='button'
              className='admin-link-btn'
              onClick={() => {
                const menuNo = row.menuNo?.trim() ?? '';
                if (!MENU_NO_PATTERN.test(menuNo)) {
                  message.warning('菜单编码须为 21 位数字');
                  return;
                }
                setEditingMenuNo(menuNo);
                setFormOpen(true);
              }}
            >
              编辑
            </button>
            <button
              type='button'
              className='admin-link-btn admin-link-btn--danger'
              onClick={() => confirmDelete(row)}
            >
              删除
            </button>
            {hasChildren ? (
              <button type='button' className='admin-link-btn' onClick={() => toggleExpand(row)}>
                {expanded ? '收起' : '展开'}
              </button>
            ) : null}
          </div>
        );
      }
    }
  ];

  return (
    <div className='admin-page-card'>
      <div className='mb-3'>
        <Button
          type='primary'
          icon={<PlusOutlined />}
          className='admin-pkg-create-btn'
          onClick={() => {
            setEditingMenuNo(null);
            setFormOpen(true);
          }}
        >
          新增
        </Button>
      </div>

      <Table<MenuItem>
        rowKey={(row) => rowKey(row)}
        columns={columns}
        dataSource={tree}
        loading={loading}
        pagination={false}
        size='middle'
        className='admin-user-table admin-menu-table'
        rowClassName={(row) => `admin-menu-row admin-menu-row--level-${getLevel(row, tree)}`}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
          childrenColumnName: 'children',
          indentSize: 0,
          showExpandColumn: false
        }}
      />

      <MenuFormModal
        open={formOpen}
        menuNo={editingMenuNo}
        tree={tree}
        submitting={submitting}
        onClose={closeForm}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
